import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Plus, Minus } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import NotFound from "@/pages/NotFound";
import { SERVICES, serviceBySlug } from "@/data/services";
import { BUSINESS, SERVICE_AREAS, breadcrumbSchema, faqSchema, serviceSchema } from "@/lib/seo";

const Mono = ({ children, className = "" }) => <p className={`font-mono-tech text-[10px] uppercase tracking-[0.3em] ${className}`}>{children}</p>;

export const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-zinc-900" data-testid={`service-faq-${idx}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-start justify-between gap-6 py-5 text-left" aria-expanded={open}>
        <h3 className="font-serif-display text-xl md:text-2xl text-white">{q}</h3>
        {open ? <Minus size={16} className="text-[#C87455] shrink-0 mt-2" /> : <Plus size={16} className="text-zinc-500 shrink-0 mt-2" />}
      </button>
      <p className={`text-zinc-400 text-sm leading-relaxed pb-6 max-w-3xl ${open ? "" : "sr-only"}`}>{a}</p>
    </div>
  );
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = serviceBySlug(slug);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!service) return;
    api.get("/products").then((r) => {
      const bySlug = Object.fromEntries(r.data.map((p) => [p.slug, p]));
      setProducts(service.products.map((s) => bySlug[s]).filter(Boolean));
    }).catch(() => {});
  }, [service]);

  if (!service) return <NotFound />;

  const path = `/services/${service.slug}`;
  const related = service.related.map(serviceBySlug).filter(Boolean);
  const cta = service.cta || { label: "Request a quote", to: "/quote" };

  return (
    <main data-testid={`service-page-${service.slug}`} className="pt-40 pb-32 min-h-screen">
      <Seo
        title={service.metaTitle}
        description={service.metaDescription}
        path={path}
        image={products[0]?.image}
        jsonLd={[
          serviceSchema({ name: service.title, description: service.metaDescription, path, serviceType: service.serviceType }),
          breadcrumbSchema([["Home", "/"], ["Services", "/services"], [service.h1, path]]),
          faqSchema(service.faqs),
        ]}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <nav aria-label="Breadcrumb" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex gap-3" data-testid="service-breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link><span>/</span>
          <Link to="/services" className="hover:text-white">Services</Link><span>/</span>
          <span className="text-zinc-300">{service.h1}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-10">
          <div className="lg:col-span-7">
            <Reveal>
              <Mono className="text-[#C87455]">{service.serviceType} · {BUSINESS.locality}, Alberta</Mono>
              <h1 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl text-white mt-4 tracking-tight leading-tight">{service.title}</h1>
              <p className="text-zinc-300 text-base md:text-lg mt-8 leading-relaxed">{service.intro}</p>
              <div className="flex flex-wrap gap-4 mt-10">
                <Link to={cta.to} data-testid="service-primary-cta" className="bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm inline-flex items-center gap-3">{cta.label} <ArrowRight size={14} /></Link>
                <Link to={service.cta?.to === "/business" || service.slug === "bulk-branded-merchandise" ? "/portfolio" : "/gallery"} data-testid="service-gallery-link" className="border border-[#A9B0B7]/50 text-[#A9B0B7] hover:bg-[#A9B0B7] hover:text-black transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm">{service.cta?.to === "/business" || service.slug === "bulk-branded-merchandise" ? "See case studies" : "See finished work"}</Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="border border-zinc-900 bg-[#0A0A0A] divide-y divide-zinc-900">
                <div className="p-6">
                  <Mono className="text-zinc-500 mb-4">Typical uses</Mono>
                  <ul className="space-y-2">{service.uses.map((u) => <li key={u} className="text-sm text-zinc-300 flex gap-2"><span className="text-[#C87455]">◆</span>{u}</li>)}</ul>
                </div>
                <div className="p-6">
                  <Mono className="text-zinc-500 mb-4">Materials &amp; products</Mono>
                  <ul className="space-y-2">{service.materials.map((m) => <li key={m} className="text-sm text-zinc-400 flex gap-2"><span className="text-zinc-600">—</span>{m}</li>)}</ul>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal>
          <section className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="font-serif-display text-3xl text-white">Options &amp; what's included</h2>
              <ul className="mt-6 space-y-3">{service.options.map((o) => <li key={o} className="text-sm text-zinc-300 leading-relaxed flex gap-3"><span className="font-mono-tech text-[#C87455] text-xs mt-0.5">+</span>{o}</li>)}</ul>
            </div>
            <div>
              <h2 className="font-serif-display text-3xl text-white">Where we work</h2>
              <p className="text-sm text-zinc-400 leading-relaxed mt-6">
                Ember &amp; Steel Studio is based in Leduc, Alberta and serves {SERVICE_AREAS.slice(0, -1).join(", ")} and {SERVICE_AREAS.at(-1)}.
                Every order ships by Canada Post across Canada, with tracked shipping to the USA — shipping is calculated at checkout.
              </p>
              <dl className="mt-6 grid grid-cols-2 gap-4 font-mono-tech text-xs">
                <div><dt className="text-zinc-600 uppercase tracking-[0.2em] text-[10px]">Turnaround</dt><dd className="text-zinc-300 mt-1">5–7 business days after proof approval</dd></div>
                <div><dt className="text-zinc-600 uppercase tracking-[0.2em] text-[10px]">Quotes</dt><dd className="text-zinc-300 mt-1">Reply within one working day</dd></div>
                <div><dt className="text-zinc-600 uppercase tracking-[0.2em] text-[10px]">Contact</dt><dd className="text-zinc-300 mt-1"><a href={`mailto:${BUSINESS.email}`} className="hover:text-[#C87455]">{BUSINESS.email}</a></dd></div>
                <div><dt className="text-zinc-600 uppercase tracking-[0.2em] text-[10px]">Phone</dt><dd className="text-zinc-300 mt-1"><a href={`tel:${BUSINESS.phone}`} className="hover:text-[#C87455]">{BUSINESS.phoneDisplay}</a></dd></div>
              </dl>
            </div>
          </section>
        </Reveal>

        {products.length > 0 && (
          <section className="mt-24" data-testid="service-products">
            <Reveal>
              <div className="flex items-end justify-between gap-6 mb-10">
                <h2 className="font-serif-display text-3xl md:text-4xl text-white">Shop pieces made with this service</h2>
                <Link to="/shop" className="link-line hidden sm:block font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white">All products →</Link>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </section>
        )}

        <section className="mt-24 max-w-4xl" data-testid="service-faqs">
          <Reveal>
            <Mono className="text-[#C87455]">Questions</Mono>
            <h2 className="font-serif-display text-3xl md:text-4xl text-white mt-3">Frequently asked</h2>
          </Reveal>
          <div className="mt-8 border-t border-zinc-900">{service.faqs.map(([q, a], i) => <FaqItem key={q} q={q} a={a} idx={i} />)}</div>
        </section>

        <section className="mt-24" data-testid="service-related">
          <Mono className="text-zinc-500 mb-6">Related services</Mono>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {related.map((r) => (
              <Link key={r.slug} to={`/services/${r.slug}`} data-testid={`related-${r.slug}`} className="bg-[#080808] hover:bg-[#0C0C0C] transition-colors p-6 block">
                <h3 className="font-serif-display text-xl text-white">{r.h1}</h3>
                <p className="text-zinc-500 text-xs mt-2 line-clamp-2">{r.metaDescription}</p>
              </Link>
            ))}
          </div>
          <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-6">
            All services: {SERVICES.map((s, i) => <span key={s.slug}><Link to={`/services/${s.slug}`} className="hover:text-[#C87455]">{s.nav}</Link>{i < SERVICES.length - 1 ? " · " : ""}</span>)}
          </p>
        </section>
      </div>
    </main>
  );
}
